import { isNull, isUndefined } from '../util/TypeUtil';
import SoundManagerEvent from './SoundManagerEvent';

export default class SoundManager extends createjs.EventDispatcher {
    constructor() {
        super();
        this._soundInstances = {};
        this._volume = 1;
        this._lastVolume = 1;
        this._pan = 0;
        this._mute = false;
        this._pause = false;
        this._nextSoundID = 0;
        this._loadedSounds = {};

        this._onStreamAudioPausedBound = this._onStreamAudioPaused.bind(this);
        this._onStreamAudioCanPlayBound = this.onStreamAudioCanPlay.bind(this);
        this._onStreamAudioWasCompletedBound = this.onStreamAudioWasCompleted.bind(this);
        this._onStreamErrorHandlerBound = this.onStreamErrorHandler.bind(this);
    };

    /**
     * Public function called on first click in project, 
     *   to ensure initialization of audio on user interaction.
     *   
     *   NOTE: iOS only streams sounds within the context of a user interaction. This can be circumvented by creating the <audio> tags
     *         within this context. In iOS8+ this only works if the <audio> is also _played_ within the context of a user interaction.
     *         Workaround for this is to play and pause a dummy (kickstarter) MP3 when initializing the <audio> tags within the user interaction
     *         context.
     *   
     *   @param streamKickStarterMp3Url URL to an MP3 file that can be used to kickstart the streaming audio. Typically a silent mp3.
     */
    initializeStreamChannels(streamKickStarterMp3Url) {
        //Check if already initialized 
        // to avoid calling this mutltiple times resulting in lost references to audio elements
        if (this._streamChannels && this._streamChannels.length) {
            console.warn("SoundManager.initializeStreamChannels already called. Make sure this is called as a result of user interaction the first time!");
            return;
        }
        console.info("SoundManager.initializeStreamChannels #:" + SoundManager.STREAM_CHANNEL_COUNT + ", url:" + streamKickStarterMp3Url);
        this._streamChannels = [];
        for (var i = 0; i < SoundManager.STREAM_CHANNEL_COUNT; i++) {
            this._streamChannels.push(document.createElement('audio'));

            // Kickstart the <audio>
            if (!isNull(streamKickStarterMp3Url)) {
                this.streamSound(streamKickStarterMp3Url, null, null, false, i);
                this.stopStreamSound();
            }
        }
    };


    /**************************************************
     *         GLOBAL / MASTER SOUND METHODS
     *************************************************/

    /**
     * Setting global volume. 
     * @param {type} value between 0 and 1
     */
    setVolume(value) {
        this._lastVolume = this._volume;

        // Set volume on CreateJS sounds
        createjs.Sound.volume = value;

        // Set volume on streaming music
        for (var i = 0; i < this._streamChannels.length; i++) {
            this._streamChannels[i].volume = value;
        }

        this._volume = value;
    };

    getVolume() {
        return this._volume;
    };

    setPan(value) {
        for (var i = 0; i < this._streamChannels.length; i++) {
            this._streamChannels[i].pan = value;
        }

        this._pan = value;
    };

    getPan() {
        return this._pan;
    };

    /**
     * Setting global mute. 
     * 
     * @param {type} value whether to mute or unmute
     */
    setMute(value) {
        // Setting mute on SoundJS
        //  This will not be applied to newly started sounds, so reapply when playing sound!
        createjs.Sound.muted = value;
        // Mute streaming channels
        if (this._streamChannels) {
            for (var i = 0; i < this._streamChannels.length; i++) {
                var streamChannel = this._streamChannels[i];
                streamChannel.muted = value;
            }
        }
        this._mute = value;
    };

    getMute() {
        return this._mute;
    };

    /**
         * Pauses all sounds that are currently playing.
         */
    pause() {
        for (var id in this._soundInstances) {
            this.pauseSound(id);
        }

        //also pause stream channels
        for (var i = 0; i < SoundManager.STREAM_CHANNEL_COUNT; i++) {
            if (this._streamChannels[i].isActive && !this._streamChannels[i].paused) {
                // Only explicitly pause active, playing streams.
                this._streamChannels[i].flagIsPaused = true;
                this.stopStreamSound(i);
            }
        }
    };

    /**
     * Resumes all sounds that are currently paused.
     */
    resume() {
        for (var id in this._soundInstances) {

            this.resumeSound(id);
        }

        for (var i = 0; i < SoundManager.STREAM_CHANNEL_COUNT; i++) {
            if (this._streamChannels[i].isActive && this._streamChannels[i].flagIsPaused)
                this.resumeStreamSound(i);
        }
    };

    /**
     * Stops playing all active sounds.
     */
    stop(includingStreamingAudio) {
        for (var soundID in this._soundInstances) {
            this.stopSound(soundID);
        }
        this._soundInstances = {};
        //stop streaming audio if explicitly specified in argument
        if (includingStreamingAudio) {
            for (var i = 0; i < SoundManager.STREAM_CHANNEL_COUNT; i++) {
                this.stopStreamSound(i);
            }
        }
    };

    getSoundID() {
        this._nextSoundID += 1;
        return String(this._nextSoundID);
    };


    /**
     * Check whether the SoundManager is currently playing any sound.
     * If you want to check whether a sound is playing by the soundID of the sound, use isSoundPlaying() instead.
     * 
     */
    isPlaying() {
        for (var id in this._soundInstances) {
            if (this.isSoundPlaying(id))
                return true;
        }
        return false;
    };


    /**************************************************
     *               SOUNDS (non-streaming)
     *************************************************/


    /**
     * Plays a sound that is included in the assets and preloaded through PreLoadJS.
     * @param {String} id The ID of the sound (which can be retrieved from the SoundFactory).
     * @param {int} delay The amount of time to delay te start of audio playback in milliseconds.
     * @param {int} offset The offset from the start of the audio to begin playback in milliseconds.
     * @param {int} loop The amount of times the sound should loop. Use -1 for infinite looping.
     * @param {int} volume The volume of the sound between 0 and 1. Uses SoundManager.volume by default.
     * @param {int} pan The panning of the sound between -1 (left) and 1 (right). Uses SoundManager.pan by default.
     * @param {int} fadeInDuration Optional fadeIn duration in msecs. Default: no fade in.
     * @param {Function} completeHandler
     * @param {Function} errorHandler
     * @param {Function} loopHandler
     * @return {String} soundID
     */
    playSound(id, completeHandler, errorHandler, delay, offset, loop, volume, pan, fadeInDuration, loopHandler) {
        console.log("SoundManager.prototype.playSound " + id);
        //RF20170424, added loopHandler support
        //RF20151110: added fadeIn support
        //RF20151110: Memoryleak: when playing sound the SoundInstance is stored in this._soundInstances.
        // but never removed when sound is complete.
        if (!id) {
            return;
        }

        delay = delay || 0;
        offset = offset || 0;
        loop = loop || 0;
        volume = volume || this._volume;
        pan = pan || this._pan;

        if (fadeInDuration) {
            var volumeGoal = volume;
            volume = .4;
        }
        var instance = createjs.Sound.play(id, {
            interrupt: createjs.Sound.INTERRUPT_NONE,
            delay: delay,
            offset: offset,
            loop: loop,
            volume: volume,
            pan: pan,
            paused: false
        });
        if (fadeInDuration) {
            instance.volume = 0;
            createjs.Tween.get(instance).wait(delay).to({ volume: volumeGoal }, fadeInDuration);
        }
        //store SoundInstance using a unique id
        var soundID = this.getSoundID();
        instance.uniqueId = soundID;
        this._soundInstances[soundID] = instance;
        //RF20151110: created SoundInstances were never removed!!!
        //	So I have added a complete handler that removes the SoundInstance.
        instance.addEventListener("complete", this.handleSoundComplete.bind(this));
        if (completeHandler)
            instance.addEventListener("complete", completeHandler);
        instance.addEventListener("failed", function() {
            console.error("[SoundManager]", "playSound - Failed to play sound with soundID: " + soundID + " and sourceID: " + id);
            if (errorHandler)
                errorHandler();
        });
        if (loopHandler != null) {
            instance.addEventListener("loop", loopHandler);
        }
        //createjs.Sound.muted is not applied to newly started sounds, so reapply
        createjs.Sound.muted = this._mute;
        return soundID;
    };

    handleSoundComplete(e) {
        var instance = e.target;
        var soundID = instance.uniqueId;
        //console.log("SoundManager.handleSoundComplete" +  soundID + "" + e.target.src);
        this._destroySound(soundID);
    }

    setSoundLoopHandler(soundID, handler) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "setSoundLoopHandler - no sound with id '" + soundID + "' exists.");
            return;
        }
        if (handler != null) {
            instance.addEventListener("loop", handler);
        }
    }

    /**
     * For advanced use, e.g. reading props like duration, position etc
     * @param {string} soundID
     * @returns {createjs.AbstractSoundInstance}
     */
    getSoundInstance(soundID) {
        return this._soundInstances[soundID];
    }



    //internal destroy function for deleting sounds. Do not use this to stop a sound, use stopSound instead.
    _destroySound(soundID) {

        var instance = this._soundInstances[soundID];
        if (!instance)
            return;

        createjs.Tween.removeTweens(instance);
        instance.removeAllEventListeners();
        //We clean up as good as we can, but in createjs.Sound 1.0.0, SoundInstances are still retained, see:
        //https://github.com/CreateJS/SoundJS/issues/259#issuecomment-368689231
        //so we manually remove the reference. This is a bit of hack, 
        //because it directly manipulates a private property of the createjs.Sound.activePlugin object.
        var src = instance.src;
        var idx = createjs.Sound.activePlugin._soundInstances[src].indexOf(instance);
        if (idx > -1) {
            createjs.Sound.activePlugin._soundInstances[src].splice(idx, 1);
        }
        //
        instance = null;
        delete this._soundInstances[soundID];
    };

    /**
     * Set the volume of the specified sound. Optionally fade to volume.
     * @param {String} soundID
     * @param {Number} value The volume value between 0 and 1.
     * @param {Number} fadeDuration Optional. Duration of fade in msecs..
     * @param {Function} onFadeComplete Optional. Callback for when fade completes.
     */
    setSoundVolume(soundID, value, fadeDuration, onFadeComplete) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "setSoundVolume - no sound with id '" + soundID + "' exists.");
            return;
        } 
        if (!fadeDuration) {
            instance.volume = value;
        }
        else {
            var tween = createjs.Tween.get(instance).to({ volume: value }, fadeDuration, createjs.Ease.none);
            if (onFadeComplete != null) tween.call(onFadeComplete, [soundID])
        }
    };

    /**
     * Set the pan of the specified sound.
     * @param {String} soundID
     * @param {Float} value The pan value between -1 and 1.
     */
    setSoundPan(soundID, value) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "setSoundPan - no sound with id '" + soundID + "' exists.");
            return;
        }
        instance.setPan(value);
    };

    /**
     * Mute or unmute the specified sound.
     * @param {String} soundID
     * @param {Boolean} value 
     */
    setSoundMute(soundID, value) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "setSoundMute - no sound with id '" + soundID + "' exists.");
            return;
        }
        instance.setMute(value);
    };

    /**
     * Pauses the specified sound.
     * @param {String} soundID
     */
    pauseSound(soundID) {
        if (this.isSoundPlaying(soundID)) {
            var instance = this._soundInstances[soundID];
            if (!instance) {
                console.error("[SoundManager]", "pauseSound - no sound with id '" + soundID + "' exists.");
                return;
            }

            instance.flagIsPaused = true;
            instance.pause();
        }
    };

    /**
     * Resumes the specified sound.
     * @param {String} soundID
     */
    resumeSound(soundID) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "resumeSound - no sound with id '" + soundID + "' exists.");
            return;
        }

        if (instance.flagIsPaused) {
            instance.flagIsPaused = false;
            console.debug('SoundManager.resumeSound', 'instance.resume()', instance.src);
            instance.resume();
        }
    };

    /**
     * Stops the specified sound.
     * @param {String} soundID
     * @param {int} fadeOutDuration in msecs, optional
     */
    stopSound(soundID, fadeOutDuration) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.info("[SoundManager]", "stopSound - no sound with id '" + soundID + "' exists.");
            return;
        }
        if (fadeOutDuration) {
            //console.log("SoundManager.stopSound, fadeOutDuration " + fadeOutDuration);
            createjs.Tween.removeTweens(instance);
            createjs.Tween.get(instance).to({ volume: 0 }, fadeOutDuration).call(this.stopSound, [soundID], this);
            return;
        }
        try {
            instance.stop();
            instance.flagIsPaused = false;
            //RF20151101: Why was this line commented out? 
            //I've uncommented it again because it constitutes a memory leak.
            this._destroySound(soundID);
        }
        catch (err) {
            console.error("[SoundManager]", "stopSound - no sound with id '" + soundID + "' exists.");
            return;
        }
    };

    /**
     * Check whether the specified sound is currently playing. Paused sounds return false.
     * @param {String} soundID 
     */
    isSoundPlaying(soundID) {
        var instance = this._soundInstances[soundID];
        if (!instance) {
            console.error("[SoundManager]", "isSoundPlaying - no sound with id '" + soundID + "' exists.");
            return false;
        }

        // Paused? return.
        return !instance.flagIsPaused && instance.playState === createjs.Sound.PLAY_SUCCEEDED;
    };

    soundExists(soundID) {
        var instance = this._soundInstances[soundID];
        return !isUndefined(instance);
    };


    /**************************************************
     *                STREAMING
     *************************************************/


    /**
     * Streams an audio file from the specified url.
     * RF 20170509: added fade support. Also see onStreamAudioCanPlay
     * @param {String} url The url of the audio file.
     * @param {Function} completeHandler
     * @param {Function} errorHandler
     * @param {int} loop Use true for infinite looping.
     * @param {int} channelIndex Optional, index of channel to play on. Default 0.
     * @param {Number} fadeDuration Optional. Fade-in duration in msecs
     * @param {Function} onFadeComplete Optional. Callback called when fade completes.
     * @return {int} index of used channel
     */
    streamSound(url, completeHandler, errorHandler, loop, channelIndex, fadeDuration, onFadeComplete) {
        if (isNull(channelIndex))
            channelIndex = 0;
        if (isUndefined(this._streamChannels))
            this.initializeStreamChannels();

        console.log("SoundManager.streamSound ", url, completeHandler, errorHandler, loop, channelIndex);
        var channel = this._streamChannels[channelIndex];
        channel.pause();
        channel.autoplay = true;
        //Create custom property to keep track of desired playstatus
        //Property is used in pause/resume, to only pause/resume active channels
        //'active' is meant as 'in use' 
        channel.isActive = true;
        // Create custom property to keep track of whether the channel was paused (using pause) or stopped (using stopStreamSound).
        channel.flagIsPaused = false;

        channel.addEventListener("pause", this._onStreamAudioPausedBound);
        channel.addEventListener("canplay", this._onStreamAudioCanPlayBound);
        channel.addEventListener("ended", this._onStreamAudioWasCompletedBound);
        channel.addEventListener("error", this._onStreamErrorHandlerBound);

        channel.src = url;
        channel.loop = loop;
        channel.volume = this.getVolume();
        channel.pan = this.getPan();

        this._streamChannelCompleteHandler = completeHandler;
        this._streamChannelsErrorHandler = errorHandler;

        // Trigger the playing when streaming for the first time. (necessary iOS 8+)
        channel.play();
        //RF 20170509 added fade support
        channel.channelIndex = channelIndex;
        channel.fadeDuration = fadeDuration;
        channel.onFadeComplete = onFadeComplete;
        //
        return channelIndex;
    };

    /**
     * Stop the stream sound. Optionally fade out.
     * RF 20170509: added fade-out support
     * @param {type} channelIndex
     * @param {Number} fadeDuration Optional. Fade-out duration in msecs
     * @param {Function} onFadeComplete Optional. Callback called when fade completes.
     * @returns {undefined}
     */
    stopStreamSound(channelIndex, fadeDuration, onFadeComplete) {
        if (fadeDuration) {
            //When fade completes, call stopStreamSound again without the fade parameters
            var onComplete = function() {
                this.stopStreamSound(channelIndex);
                if (onFadeComplete != null) onFadeComplete(channelIndex);
            }
            this.fadeStreamSound(channelIndex, 0, fadeDuration, onComplete);
        }
        else {
            if (isNull(channelIndex)) channelIndex = 0;
            var channel = this._streamChannels[channelIndex];
            channel.removeEventListener("pause", this._onStreamAudioPausedBound);
            channel.removeEventListener("canplay", this._onStreamAudioCanPlayBound);
            channel.removeEventListener("ended", this._onStreamAudioWasCompletedBound);
            channel.removeEventListener("error", this._onStreamErrorHandlerBound);
            channel.pause();
        }
    };

    resumeStreamSound(channelIndex) {
        if (isNull(channelIndex))
            channelIndex = 0;
        var channel = this._streamChannels[channelIndex];
        channel.addEventListener("pause", this._onStreamAudioPausedBound);
        channel.addEventListener("canplay", this._onStreamAudioCanPlayBound);
        channel.addEventListener("ended", this._onStreamAudioWasCompletedBound);
        channel.addEventListener("error", this._onStreamErrorHandlerBound);
        channel.play();
    };

    setStreamSoundVolume(channelIndex, volume, fadeDuration, onFadeComplete) {
        if (isNull(channelIndex))
            channelIndex = 0;
        var channel = this._streamChannels[channelIndex];
        //
        if (fadeDuration) {
            channel.onFadeComplete = onFadeComplete;
            var tween = createjs.Tween.get(channel).to({ volume: volume }, fadeDuration, createjs.Ease.none);
            if (onComplete != null) tween.call(onComplete, [channelIndex])
        }
        else {
            channel.volume = volume;
        }
    };

    getChannel(channelIndex) {
        return this._streamChannels[channelIndex];
    };

    _onStreamAudioPaused(event) {
        // Check if audio is complete, since the 'pause' event is always called before the 'ended' event
        if (!event.target.ended) {
            event.target.flagIsPaused = true;
            this.dispatchEvent(SoundManagerEvent.AUDIO_PAUSED);
        }
    };

    onStreamAudioCanPlay(event) {
        var channel = event.target;
        channel.removeEventListener("canplay", this._onStreamAudioCanPlayBound);
        channel.play();
        channel.isActive = true;
        //fadeDuration and onFadeComplete set in streamSound
        if (channel.fadeDuration) {
            channel.volume = 0;
            this.fadeStreamSound(channel.channelIndex, 1, channel.fadeDuration, channel.onFadeComplete);
        }
    };

    onStreamAudioWasCompleted(event) {
        var channel = event.target;
        channel.removeEventListener("canplay", this._onStreamAudioCanPlayBound);
        channel.removeEventListener("pause", this._onStreamAudioPausedBound);
        channel.removeEventListener("ended", this._onStreamAudioWasCompletedBound);
        channel.removeEventListener("error", this._onStreamErrorHandlerBound);
        if (!isNull(this._streamChannelCompleteHandler)) {
            this._streamChannelCompleteHandler();
        }
        channel.isActive = false;
    };

    onStreamErrorHandler(event) {
        var channel = event.target;
        channel.removeEventListener("error", this._onStreamAudioWasCompletedBound);
        channel.removeEventListener("ended", this._onStreamErrorHandlerBound);
        if (!isNull(this._streamChannelsErrorHandler)) {
            this._streamChannelsErrorHandler();
        }
        channel.isActive = false;
    };


    /**************************************************
     *            VARIOUS / GENERAL
     *************************************************/

    /**
     * Output a report to console, for debugging.
     */
    report() {
        var lines = ["SoundManager.report"];
        lines.push("_streamChannels length: " + (this._streamChannels ? this._streamChannels.length : "not initialized"));
        lines.push("_volume: " + this._volume + ", _lastVolume: " + this._lastVolume + ", _pan: " + this._pan + ", _mute: " + this._mute + ", _nextSoundID: " + this._nextSoundID);
        lines.push("_soundInstances: ");
        for (var s in this._soundInstances) {
            var inst = this._soundInstances[s];
            lines.push("\t" + s + ", src: " + inst.src + ", volume: " + inst.volume + ", loop: " + inst.loop + ", duration: " + Math.round(inst.duration) + ", position: " + Math.round(inst.position) + ", muted: " + inst.muted + ", playState: " + inst.playState);

        }
        console.log(lines.join("\n"));
    }

}

// Currently: 4 channels of streaming sounds
SoundManager.STREAM_CHANNEL_COUNT = 4;